import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Spinner,
  TextInput,
  Title,
} from '@patternfly/react-core';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { ExternalLinkAltIcon, SyncIcon } from '@patternfly/react-icons';
import { Link } from 'react-router-dom';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import {
  PlayerSessionRow,
  UserOverviewRow,
  parsePlayerSessionsResponse,
  parseUserOverviewResponse,
} from 'src/contracts/reporting';
import './reporting.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface ReportFilters {
  fromValue: string;
  toValue: string;
  gameType: string;
  robotId: string;
}

const EMPTY_FILTERS: ReportFilters = {
  fromValue: '',
  toValue: '',
  gameType: '',
  robotId: '',
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const toIsoIfSet = (value: string) => {
  if (!value.trim()) {
    return undefined;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
};

const appendFilters = (params: URLSearchParams, filters: ReportFilters) => {
  const fromIso = toIsoIfSet(filters.fromValue);
  const toIso = toIsoIfSet(filters.toValue);
  if (fromIso) {
    params.set('from', fromIso);
  }
  if (toIso) {
    params.set('to', toIso);
  }
  if (filters.gameType.trim()) {
    params.set('game_type', filters.gameType.trim());
  }
  if (filters.robotId.trim()) {
    params.set('robot_id', filters.robotId.trim());
  }
};

const UserGridReportingDashboard = () => {
  const authedFetch = useAuthedFetch();

  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [gameType, setGameType] = useState('');
  const [robotId, setRobotId] = useState('');

  const [users, setUsers] = useState<UserOverviewRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [sessions, setSessions] = useState<PlayerSessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentFilters = useMemo<ReportFilters>(
    () => ({
      fromValue,
      toValue,
      gameType,
      robotId,
    }),
    [fromValue, toValue, gameType, robotId],
  );

  const fetchUsers = useCallback(
    async (filters: ReportFilters): Promise<UserOverviewRow[]> => {
      setUsersLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('page_size', '100');
        appendFilters(params, filters);
        const response = await authedFetch(`${API_BASE}/api/reporting/users/overview?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`User report load failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parseUserOverviewResponse(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setUsers(parsed.value.items);
        setError(null);
        return parsed.value.items;
      } catch (err) {
        setError((err as Error).message);
        setUsers([]);
        return [];
      } finally {
        setUsersLoading(false);
      }
    },
    [authedFetch],
  );

  const fetchSessions = useCallback(
    async (userId: string, filters: ReportFilters) => {
      setSessionsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('page_size', '100');
        params.set('user_id', userId);
        appendFilters(params, filters);
        const response = await authedFetch(`${API_BASE}/api/reporting/players/sessions?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Session list load failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parsePlayerSessionsResponse(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setSessions(parsed.value.items);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    },
    [authedFetch],
  );

  const applyFilters = useCallback(async () => {
    const userRows = await fetchUsers(currentFilters);
    if (!selectedUserId) {
      return;
    }
    const selectedStillVisible = userRows.some((row) => row.user_id === selectedUserId);
    if (!selectedStillVisible) {
      setSelectedUserId(null);
      setSessions([]);
      return;
    }
    await fetchSessions(selectedUserId, currentFilters);
  }, [currentFilters, fetchSessions, fetchUsers, selectedUserId]);

  useEffect(() => {
    void fetchUsers(EMPTY_FILTERS);
  }, [fetchUsers]);

  const onSelectUser = useCallback(
    async (user: UserOverviewRow) => {
      setSelectedUserId(user.user_id);
      await fetchSessions(user.user_id, currentFilters);
    },
    [currentFilters, fetchSessions],
  );

  const onClearFilters = useCallback(async () => {
    setFromValue('');
    setToValue('');
    setGameType('');
    setRobotId('');
    const userRows = await fetchUsers(EMPTY_FILTERS);
    if (!selectedUserId) {
      return;
    }
    const selectedStillVisible = userRows.some((row) => row.user_id === selectedUserId);
    if (!selectedStillVisible) {
      setSelectedUserId(null);
      setSessions([]);
      return;
    }
    await fetchSessions(selectedUserId, EMPTY_FILTERS);
  }, [fetchSessions, fetchUsers, selectedUserId]);

  const selectedUser = useMemo(
    () => users.find((user) => user.user_id === selectedUserId) || null,
    [users, selectedUserId],
  );

  return (
    <div className="reporting-page">
      <PageHeader title="User Reporting" subtitle="User grid rollups with session drill-down" />

      <Card className="reporting-filter-card">
        <CardTitle>Filters</CardTitle>
        <CardBody>
          <Grid hasGutter>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                type="datetime-local"
                value={fromValue}
                onChange={(_, value) => setFromValue(value)}
                aria-label="From"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                type="datetime-local"
                value={toValue}
                onChange={(_, value) => setToValue(value)}
                aria-label="To"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                value={gameType}
                onChange={(_, value) => setGameType(value)}
                aria-label="Game type"
                placeholder="game_type"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                value={robotId}
                onChange={(_, value) => setRobotId(value)}
                aria-label="Robot ID"
                placeholder="robot UUID"
              />
            </GridItem>
          </Grid>
          <div className="reporting-filter-actions">
            <Flex gap={{ default: 'gapSm' }} flexWrap={{ default: 'wrap' }}>
              <FlexItem>
                <Button onClick={() => void applyFilters()}>Apply filters</Button>
              </FlexItem>
              <FlexItem>
                <Button variant="secondary" icon={<SyncIcon />} onClick={() => void applyFilters()}>
                  Refresh
                </Button>
              </FlexItem>
              <FlexItem>
                <Button variant="link" onClick={() => void onClearFilters()}>
                  Clear filters
                </Button>
              </FlexItem>
            </Flex>
          </div>
        </CardBody>
      </Card>

      <Card className="reporting-table-card">
        <CardTitle>
          <Title headingLevel="h3" size="lg">
            Users
          </Title>
        </CardTitle>
        <CardBody>
          {error && <div className="reporting-error">{error}</div>}

          {usersLoading ? (
            <div className="reporting-loading">
              <Spinner size="xl" />
            </div>
          ) : users.length === 0 ? (
            <EmptyState>
              <Title headingLevel="h4">No users</Title>
              <EmptyStateBody>No reporting users matched the selected filters.</EmptyStateBody>
            </EmptyState>
          ) : (
            <div className="reporting-table-wrap">
              <table className="reporting-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Sessions</th>
                    <th>Attempts</th>
                    <th>Last Play</th>
                    <th>Wins / Losses</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const selected = selectedUserId === user.user_id;
                    return (
                      <tr key={user.user_id} className={selected ? 'reporting-selected-row' : undefined}>
                        <td>
                          <div>{user.email || user.display_name || user.user_id}</div>
                          <div className="reporting-muted">{user.user_id}</div>
                        </td>
                        <td>{user.session_count}</td>
                        <td>{user.attempt_count}</td>
                        <td>{formatDateTime(user.last_play_at)}</td>
                        <td>
                          {user.win_count} / {user.loss_count}
                        </td>
                        <td>
                          <Button variant={selected ? 'secondary' : 'link'} onClick={() => void onSelectUser(user)}>
                            {selected ? 'Selected' : 'View sessions'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="reporting-table-card">
        <CardTitle>
          <Flex alignItems={{ default: 'alignItemsCenter' }} justifyContent={{ default: 'justifyContentSpaceBetween' }}>
            <FlexItem>
              <Title headingLevel="h3" size="lg">
                Sessions
              </Title>
            </FlexItem>
            <FlexItem>
              {selectedUser ? (
                <Label color="blue">{selectedUser.email || selectedUser.display_name || selectedUser.user_id}</Label>
              ) : (
                <Label color="grey">Select a user</Label>
              )}
            </FlexItem>
          </Flex>
        </CardTitle>
        <CardBody>
          {!selectedUser ? (
            <EmptyState>
              <Title headingLevel="h4">No user selected</Title>
              <EmptyStateBody>Select a user row to view sessions and open session details.</EmptyStateBody>
            </EmptyState>
          ) : sessionsLoading ? (
            <div className="reporting-loading">
              <Spinner size="xl" />
            </div>
          ) : sessions.length === 0 ? (
            <EmptyState>
              <Title headingLevel="h4">No sessions</Title>
              <EmptyStateBody>The selected user has no sessions in the current filter window.</EmptyStateBody>
            </EmptyState>
          ) : (
            <div className="reporting-table-wrap">
              <table className="reporting-table">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Started</th>
                    <th>Attempts</th>
                    <th>Result</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.session_id}>
                      <td>
                        <span className="reporting-mono">{session.session_id}</span>
                      </td>
                      <td>{formatDateTime(session.session_started_at || session.queued_at)}</td>
                      <td>{session.attempt_count}</td>
                      <td>{session.session_result?.kind || '—'}</td>
                      <td>{session.state}</td>
                      <td>
                        <Link to={`/sessions/${session.session_id}`}>
                          <Button variant="link" icon={<ExternalLinkAltIcon />}>
                            Open details
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default UserGridReportingDashboard;
